import { api, ApiError } from '../../api/client';
import type { PendingPhoto, RequestDraft } from './model';
import { getDisplayCurrency } from '../../i18n/preferences';

interface FileAsset {
  id: number;
  url: string;
  original_filename: string;
  content_type: string;
}

export interface RequestRecord {
  id: number;
  public_code: string | null;
  status: string;
  from_city: string;
  to_city: string;
}

function moneyToMinor(value: string): number | null {
  const normalized = value.replace(/[^\d]/g, '');
  return normalized ? Number(normalized) * 100 : null;
}

function requestPayload(draft: RequestDraft, photoAssetIds: number[]) {
  return {
    from_city: draft.fromCity.trim(),
    to_city: draft.toCity.trim(),
    pickup_not_before: draft.pickupNotBefore || null,
    delivery_not_after: draft.deliveryNotAfter || null,
    urgency: draft.urgency,
    carrier_box: draft.carrierBox,
    documents_status: draft.documentsStatus,
    pet_notes: draft.petNotes,
    feeding_notes: draft.feedingNotes,
    pickup_contact_name: draft.pickupContactName,
    pickup_contact_phone: draft.pickupContactPhone,
    pickup_contact_notes: draft.pickupContactNotes,
    delivery_contact_name: draft.deliveryContactName,
    delivery_contact_phone: draft.deliveryContactPhone,
    delivery_contact_notes: draft.deliveryContactNotes,
    budget_min_minor: draft.budgetMode === 'fixed' ? moneyToMinor(draft.budgetMin) : null,
    budget_max_minor: draft.budgetMode === 'fixed' ? moneyToMinor(draft.budgetMax) : null,
    currency: getDisplayCurrency(),
    photo_ids: photoAssetIds,
    pets: [
      {
        pet_type: draft.petType,
        custom_type: draft.petType === 'other' ? draft.customType : '',
        breed: draft.breed,
        count: Math.max(1, draft.count || 1),
        weight_kg: draft.weightKg || null,
        age: draft.age,
        name: draft.petName,
      },
    ],
  };
}

import { optimizeImageForUpload } from '../../utils/imageCompressor';

export async function uploadRequestPhotos(photos: PendingPhoto[]): Promise<number[]> {
  const assetIds: number[] = [];
  for (const photo of photos) {
    const rawFile = new File([photo.blob], photo.name, { type: photo.type, lastModified: photo.lastModified });
    const optimized = await optimizeImageForUpload(rawFile);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', 'pet_photo');
    body.append('visibility', 'deal');
    const asset = await api<FileAsset>('/api/media/', { method: 'POST', body });
    assetIds.push(asset.id);
  }
  return assetIds;
}

export async function saveRequestOnServer(
  draft: RequestDraft,
  photoAssetIds: number[],
): Promise<RequestRecord> {
  const payload = requestPayload(draft, photoAssetIds);
  if (draft.requestId) {
    try {
      return await api<RequestRecord>(`/api/requests/drafts/${draft.requestId}/`, {
        method: 'PATCH',
        body: payload,
      });
    } catch (error) {
      // A previously published request is no longer available through the
      // drafts endpoint. Start a fresh draft while preserving the form data.
      if (!(error instanceof ApiError) || error.status !== 404) throw error;
    }
  }
  return api<RequestRecord>('/api/requests/drafts/', { method: 'POST', body: payload });
}

export function publishRequest(requestId: number): Promise<RequestRecord> {
  return api<RequestRecord>(`/api/requests/${requestId}/publish/`, { method: 'POST' });
}
